import React from "react";

type PaginationProps = {
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  hasNext: boolean;
  hasPrevious: boolean;
  setPage: React.Dispatch<React.SetStateAction<number>>;
};

const Pagination = ({
  page,
  size,
  totalElements,
  totalPages,
  first,
  last,
  hasNext,
  hasPrevious,
  setPage,
}: PaginationProps) => {
  // Generate page numbers
  const getPages = () => {
    const pages: number[] = [];

    const start = Math.max(0, page - 1);
    const end = Math.min(totalPages - 1, page + 1);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  };

  return (
    <div className="w-full flex flex-col sm:flex-row items-center justify-between gap-4 mt-6">
      {/* Left Info */}
      <div className="text-sm text-slate-400 text-center sm:text-left">
        Showing{" "}
        <span className="font-semibold text-cyan-400">
          {page * size + 1}
        </span>{" "}
        to{" "}
        <span className="font-semibold text-cyan-400">
          {Math.min((page + 1) * size, totalElements)}
        </span>{" "}
        of{" "}
        <span className="font-semibold text-slate-200">
          {totalElements}
        </span>{" "}
        results
      </div>

      {/* Pagination */}
      <div className="flex items-center gap-2 flex-wrap justify-center">
        {/* Prev */}
        <button
          disabled={!hasPrevious}
          onClick={() => setPage(page - 1)}
          className={`
            px-4 py-2 rounded-xl border text-sm font-medium transition-all
            ${
              hasPrevious
                ? "bg-slate-900 hover:bg-slate-800 text-slate-300 border-slate-800 hover:border-slate-700"
                : "bg-slate-950 text-slate-600 cursor-not-allowed border-slate-900"
            }
          `}
        >
          Prev
        </button>

        {/* Page Numbers */}
        {getPages().map((p) => (
          <button
            key={p}
            onClick={() => setPage(p)}
            className={`
              min-w-[40px] h-10 px-3 rounded-xl text-sm font-semibold transition-all
              ${
                page === p
                  ? "bg-cyan-600 text-white shadow-lg shadow-cyan-950/50 border border-cyan-500/30"
                  : "bg-slate-900 border border-slate-800 text-slate-300 hover:bg-slate-800 hover:text-cyan-400 hover:border-slate-700"
              }
            `}
          >
            {p + 1}
          </button>
        ))}

        {/* Next */}
        <button
          disabled={!hasNext}
          onClick={() => setPage(page + 1)}
          className={`
            px-4 py-2 rounded-xl border text-sm font-medium transition-all
            ${
              hasNext
                ? "bg-slate-900 hover:bg-slate-800 text-slate-300 border-slate-800 hover:border-slate-700"
                : "bg-slate-950 text-slate-600 cursor-not-allowed border-slate-900"
            }
          `}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default Pagination;